import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Shield, Eye, EyeOff } from "lucide-react";
import JSEncrypt from "jsencrypt";

const secureZeroArray = (array) => {
  if (array && array.length) {
    for (let i = 0; i < array.length; i++) {
      array[i] = 0;
    }
  }
};

const SecureInput = ({
  pemKey,
  onChange,
  label,
  placeholder,
  value = "",
  error,
  type = "text",
  maskOnBlur = true,
  showLockIcon = true,
  maxLength,
  pattern,
  validator,
  required = false,
  className = "",
  id,
}) => {
  const inputRef = useRef(null);
  const [displayValue, setDisplayValue] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const encryptorRef = useRef(null);
  const sensitiveBufferRef = useRef(null);

  useEffect(() => {
    if (pemKey) {
      encryptorRef.current = new JSEncrypt();
      encryptorRef.current.setPublicKey(pemKey);
    }

    return () => {
      if (sensitiveBufferRef.current) {
        secureZeroArray(sensitiveBufferRef.current);
        sensitiveBufferRef.current = null;
      }
    };
  }, [pemKey]);

  const encryptValue = useCallback((plaintext) => {
    if (!encryptorRef.current || !plaintext) {
      return null;
    }

    try {
      // Use OAEP padding with SHA-256 for better security
      return encryptorRef.current.encryptOAEP(plaintext);
    } catch (error) {
      console.error("Encryption failed:", error);
      return null;
    }
  }, []);

  const handleInputChange = (e) => {
    const rawValue = e.target.value;

    // Apply pattern if provided
    let processedValue = rawValue;
    if (pattern) {
      processedValue = rawValue.replace(pattern, "");
    }

    // Apply maxLength if provided
    if (maxLength && processedValue.length > maxLength) {
      return;
    }

    setDisplayValue(processedValue);

    // Clear previous sensitive data
    if (sensitiveBufferRef.current) {
      secureZeroArray(sensitiveBufferRef.current);
    }

    // Store in secure buffer
    sensitiveBufferRef.current = new Uint8Array(processedValue.length);
    for (let i = 0; i < processedValue.length; i++) {
      sensitiveBufferRef.current[i] = processedValue.charCodeAt(i);
    }

    // Validate if validator provided
    let valid = true;
    if (validator) {
      const validationResult = validator(processedValue);
      if (typeof validationResult === "boolean") {
        valid = validationResult;
      } else if (typeof validationResult === "object") {
        valid = validationResult.valid;
      }
    }
    setIsValid(valid);

    // Encrypt and send if valid
    if (onChange) {
      const encrypted = encryptValue(processedValue);
      
      onChange({
        encryptedValue: encrypted,
        plainLength: processedValue.length,
        isValid: valid,
      });

      // Clear sensitive data after encryption
      setTimeout(() => {
        if (sensitiveBufferRef.current) {
          secureZeroArray(sensitiveBufferRef.current);
          sensitiveBufferRef.current = null;
        }
      }, 100);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const preventActions = (e) => e.preventDefault();

    // Prevent copy/paste/etc
    input.addEventListener("contextmenu", preventActions);
    input.addEventListener("copy", preventActions);
    input.addEventListener("cut", preventActions);
    input.addEventListener("paste", preventActions);

    // Prevent password managers
    input.setAttribute("autocomplete", "off");
    input.setAttribute("autocorrect", "off");
    input.setAttribute("autocapitalize", "off");
    input.setAttribute("spellcheck", "false");
    input.setAttribute("data-lpignore", "true");
    input.setAttribute("data-1p-ignore", "true");
    input.setAttribute("data-bwignore", "true");
    input.setAttribute("data-form-type", "other");
    input.setAttribute("data-dashlane-ignore", "true");
    input.setAttribute("data-lastpass-ignore-autofill", "true");

    return () => {
      input.removeEventListener("contextmenu", preventActions);
      input.removeEventListener("copy", preventActions);
      input.removeEventListener("cut", preventActions);
      input.removeEventListener("paste", preventActions);
    };
  }, []);

  const shouldMask = type === "password" && !showPassword && displayValue;

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`relative ${className}`}
    >
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-300 mb-2"
        >
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          name={`secure-input-${Date.now()}`}
          type={shouldMask ? "password" : "text"}
          placeholder={placeholder}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={`
            w-full px-4 py-3 pr-12
            bg-gray-800 border rounded-lg
            text-white placeholder-gray-500
            transition-all duration-200
            ${isFocused 
              ? "border-blue-500 ring-2 ring-blue-500/20" 
              : error 
                ? "border-red-500" 
                : "border-gray-700 hover:border-gray-600"
            }
            focus:outline-none
            ${maskOnBlur && !isFocused && displayValue ? "text-security-disc" : ""}
          `}
          style={{
            WebkitTextSecurity: maskOnBlur && !isFocused && displayValue ? "disc" : "none",
            fontFamily: type === "password" ? "monospace" : "inherit",
            letterSpacing: type === "password" ? "2px" : "normal",
          }}
        />
        
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {type === "password" && displayValue && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400 hover:text-gray-300 transition-colors"
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          )}
          
          {showLockIcon && (
            <Shield className="w-4 h-4 text-blue-500" />
          )}
        </div>
      </div>

      {error && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-2 text-sm text-red-400"
        >
          {error}
        </motion.p>
      )}
    </motion.div>
  );
};

export default SecureInput;